import React from 'react';
import { 
  Shield, 
  Server, 
  Clock, 
  Users, 
  CheckCircle,
  Key,
  Mail,
  Search,
  Eye,
  Bell,
  BarChart,
  UserPlus,
  ShoppingCart,
  Cloud,
  Zap,
  ArrowRight,
  Lock,
  ShieldCheck,
  Laptop,
  UserCog
} from 'lucide-react';

const StatCard = ({ title, value }: { title: string; value: string }) => (
  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
    <div className="text-3xl font-bold text-gray-900 mb-2">{value}</div>
    <div className="text-gray-600">{title}</div>
  </div>
);

const SolutionList = ({ title, items }: { title: string, items: { icon: any, title: string }[] }) => (
  <div className="group relative h-full">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-blue-700/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
    <div className="relative bg-white p-8 rounded-2xl border border-gray-100 shadow-lg group-hover:shadow-2xl transition-all duration-500 overflow-hidden h-full">
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-blue-100 rounded-bl-[100%] opacity-30 group-hover:opacity-50 transition-opacity -rotate-6"></div>
      
      <h3 className="text-2xl font-bold text-gray-900 mb-8">
        {title}
      </h3>
      
      <ul className="space-y-5">
        {items.map((item, index) => (
          <li 
            key={index} 
            className="flex items-center group/item transform hover:translate-x-2 transition-all duration-300"
          >
            <div className="bg-blue-50 p-2 rounded-lg group-hover/item:bg-blue-100 transition-colors mr-4 relative">
              <div className="absolute inset-0 bg-blue-600/10 rounded-lg transform scale-0 group-hover/item:scale-100 transition-transform"></div>
              <item.icon className="h-5 w-5 text-blue-600 relative z-10" />
            </div>
            <span className="text-gray-700 group-hover/item:text-blue-600 transition-colors font-medium">{item.title}</span>
          </li>
        ))}
      </ul>
      
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-blue-50 to-transparent rounded-tl-[100%] opacity-0 group-hover:opacity-30 transition-opacity rotate-6"></div>
    </div>
  </div>
);

export default function Features() {
  const stats = [
    { value: "99.9%", title: "Uptime" },
    { value: "15min", title: "Response Time" },
    { value: "24/7", title: "Support" },
    { value: "500+", title: "Clients" }
  ];

  const networkProtection = [
    { icon: Lock, title: "Multi-factor Authentication (DUO)" },
    { icon: ShieldCheck, title: "Next-Generation Anti-virus Software" },
    { icon: Search, title: "Continual Vulnerability Scans" },
    { icon: Eye, title: "Dark Web Monitoring" },
    { icon: Bell, title: "Managed Firewall and 24/7 Monitoring" },
    { icon: Shield, title: "System Monitoring and Response" }
  ];

  const leadershipSupport = [
    { icon: UserCog, title: "Assigned Virtual Chief Technology Officer (vCTO)" },
    { icon: Users, title: "Assigned Technical Alignment Manager (TAM)" },
    { icon: BarChart, title: "Quarterly Business Reviews (QBR)" },
    { icon: UserPlus, title: "Onboarding/Offboarding Support" },
    { icon: ShoppingCart, title: "Hardware/Software Purchasing Support" }
  ];

  return (
    <section className="relative bg-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <StatCard key={index} {...stat} />
          ))}
        </div>

        {/* Solutions Section */}
        <div className="relative mb-12">
          <div className="text-center mb-16">
            <div className="inline-block relative">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                We're Here To Help
              </h2>
              <div className="absolute -bottom-2 left-0 w-full h-1 bg-blue-600"></div>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-6">
              Comprehensive IT solutions designed to protect and optimize your business
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12">
            <SolutionList title="How We Protect Your Network" items={networkProtection} />
            <SolutionList title="Vision and Leadership Support" items={leadershipSupport} />
          </div>
        </div>
      </div>
    </section>
  );
}